// src/utilidades/temaVisual.js
/**
 * Tema visual de marca blanca.
 *
 * Fuente única para aplicar el color y el modo de un gimnasio al documento.
 * La paleta completa (neutros, estados, sombras) vive en
 * estilos/sistema/tokens.css y reacciona a:
 *   - --color-primario / --color-secundario (inyectados aquí)
 *   - html[data-modo="claro" | "oscuro"]
 *
 * Además calcula dos colores que CSS no puede decidir solo:
 *   - --ag-sobre-primario: texto legible encima del color de marca
 *     (blanco o tinta oscura, el de mayor contraste).
 *   - --ag-primario-texto: el color de marca ajustado hasta tener contraste
 *     AA (4.5:1) como texto sobre la superficie del modo activo.
 *   - Las tintas del menú lateral (fondo = color secundario) y el fondo de la
 *     píldora activa, también con 4.5:1 para texto pequeño.
 * Así un gym con marca amarilla o celeste sigue teniendo botones y enlaces
 * legibles.
 */

export const COLORES_TEMA_DEFAULT = Object.freeze({
  primario: '#DC2626',
  secundario: '#991B1B',
  modo: 'claro'
});

/** Fondo de la app por modo (manifest PWA y barra del navegador). */
export const FONDO_APP_MODO = Object.freeze({
  claro: '#F4F5F7',
  oscuro: '#131419'
});

/** Superficie de contenido por modo, en hex, para medir contraste. */
const SUPERFICIE_MODO = Object.freeze({
  claro: '#FFFFFF',
  oscuro: '#1B1C21'
});

export const CLAVE_TEMA_GUARDADO = 'agTema';

const BLANCO = [255, 255, 255];
const TINTA = [20, 22, 27];

export const hexARgb = (hex) => {
  if (typeof hex !== 'string') return null;
  let valor = hex.trim().replace(/^#/, '');
  if (/^[0-9a-f]{3}$/i.test(valor)) valor = valor.split('').map((c) => c + c).join('');
  if (!/^[0-9a-f]{6}$/i.test(valor)) return null;
  return [0, 2, 4].map((i) => parseInt(valor.slice(i, i + 2), 16));
};

const rgbAHex = (rgb) => `#${rgb.map((c) => Math.round(c).toString(16).padStart(2, '0')).join('')}`.toUpperCase();

const canalLineal = (c) => {
  const s = c / 255;
  return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
};

export const luminancia = (rgb) => {
  const [r, g, b] = rgb.map(canalLineal);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

export const contraste = (a, b) => {
  const [l1, l2] = [luminancia(a), luminancia(b)].sort((x, y) => y - x);
  return (l1 + 0.05) / (l2 + 0.05);
};

const mezclar = (rgb, destino, t) => rgb.map((c, i) => c + (destino[i] - c) * t);
const NEGRO = [0, 0, 0];

/** Tinta atenuada hacia el fondo, pero nunca por debajo del contraste mínimo.
 *  `objetivo` es la proporción de tinta deseada (1 = tinta pura). */
const tintaAtenuada = (tinta, fondo, objetivo, minimo = 4.5) => {
  for (let t = 1 - objetivo; t > 0; t -= 0.02) {
    const candidato = mezclar(tinta, fondo, t);
    if (contraste(candidato, fondo) >= minimo) return rgbAHex(candidato);
  }
  return rgbAHex(tinta);
};

/** Fondo de marca para texto pequeño: si el texto que le corresponde es
 *  blanco y no llega a 4.5:1, se oscurece la marca lo justo (rojo puro →
 *  rojo algo más profundo), sin cambiar su carácter. */
const fondoMarcaLegible = (rgb, minimo = 4.5) => {
  if (colorSobre(rgbAHex(rgb)) !== '#FFFFFF') return rgbAHex(rgb);
  for (let t = 0; t <= 0.6; t += 0.02) {
    const candidato = mezclar(rgb, NEGRO, t);
    if (contraste(candidato, BLANCO) >= minimo) return rgbAHex(candidato);
  }
  return rgbAHex(mezclar(rgb, NEGRO, 0.6));
};

/** Blanco o tinta oscura: el que mejor se lee sobre el color dado. */
export const colorSobre = (hex, minimo = 3) => {
  const rgb = hexARgb(hex);
  if (!rgb) return '#FFFFFF';
  // Se favorece el blanco salvo que la tinta oscura sea claramente mejor:
  // en marcas medias (rojo, azul) el blanco es la convención esperada.
  // `minimo` sube a 4.5 cuando el color es el fondo de texto pequeño (menú).
  return contraste(rgb, BLANCO) >= minimo || contraste(rgb, BLANCO) >= contraste(rgb, TINTA)
    ? '#FFFFFF'
    : rgbAHex(TINTA);
};

/** El color de marca oscurecido (modo claro) o aclarado (modo oscuro)
 *  lo justo para leerse como texto sobre la superficie. */
export const colorTextoLegible = (hex, modo = 'claro', minimo = 4.5) => {
  const rgb = hexARgb(hex);
  const fondo = hexARgb(SUPERFICIE_MODO[modo] || SUPERFICIE_MODO.claro);
  if (!rgb) return modo === 'oscuro' ? '#F87171' : '#B91C1C';
  const destino = modo === 'oscuro' ? BLANCO : TINTA;
  for (let t = 0; t <= 1; t += 0.05) {
    const candidato = mezclar(rgb, destino, t);
    if (contraste(candidato, fondo) >= minimo) return rgbAHex(candidato);
  }
  return rgbAHex(destino);
};

/** Variables de marca que tokens.css ya no define (se escriben inline). */
const VARIABLES_HEREDADAS = [
  '--color-primario-hover', '--color-primario-light',
  '--color-fondo-app', '--color-superficie', '--color-texto', '--color-texto-claro',
  '--color-borde', '--sombra', '--color-fondo-hover', '--color-fondo-tabla',
  '--color-exito-bg', '--color-exito-texto', '--color-peligro-bg', '--color-peligro-texto',
  '--color-advertencia-bg', '--color-advertencia-texto', '--color-urgencia-bg',
  '--color-urgencia-texto', '--color-info-bg', '--color-info-texto',
  '--color-yape-bg', '--color-yape-texto'
];

/**
 * Variables CSS calculadas para un par de colores y un modo (función pura).
 * El menú lateral usa el secundario de fondo: sus tintas se calculan aquí
 * porque color-mix no garantiza contraste.
 */
export const variablesTema = (colores = {}) => {
  const primario = hexARgb(colores.primario) ? colores.primario : COLORES_TEMA_DEFAULT.primario;
  const secundario = hexARgb(colores.secundario) ? colores.secundario : COLORES_TEMA_DEFAULT.secundario;
  const modo = colores.modo === 'oscuro' ? 'oscuro' : 'claro';
  const fondoMenu = hexARgb(secundario);
  const tintaMenu = colorSobre(secundario, 4.5);
  return {
    modo,
    variables: {
      '--color-primario': primario,
      '--color-secundario': secundario,
      '--ag-sobre-primario': colorSobre(primario),
      '--ag-sobre-secundario': tintaMenu,
      '--ag-primario-texto': colorTextoLegible(primario, modo),
      '--ag-menu-tinta-2': tintaAtenuada(hexARgb(tintaMenu), fondoMenu, 0.74),
      '--ag-menu-tinta-3': tintaAtenuada(hexARgb(tintaMenu), fondoMenu, 0.6),
      '--ag-primario-menu': fondoMarcaLegible(hexARgb(primario)),
    },
  };
};

/**
 * Aplica marca y modo al documento. Idempotente; seguro en SSR/tests.
 * @param {{ primario?: string, secundario?: string, modo?: 'claro'|'oscuro' }} colores
 * @param {{ persistir?: boolean }} opciones  persistir=false para páginas
 *   públicas (catálogo), que no deben cambiar el tema guardado del panel.
 */
export const aplicarTema = (colores = {}, { persistir = true } = {}) => {
  if (typeof document === 'undefined') return;
  const { modo, variables } = variablesTema(colores);
  const root = document.documentElement;

  // Limpia overrides inline antiguos para que la hoja de tokens mande.
  VARIABLES_HEREDADAS.forEach((nombre) => root.style.removeProperty(nombre));
  if (document.body) document.body.style.removeProperty('background-color');

  root.dataset.modo = modo;
  root.style.colorScheme = modo === 'oscuro' ? 'dark' : 'light';
  Object.entries(variables).forEach(([nombre, valor]) => root.style.setProperty(nombre, valor));

  // El script previo a React (public/index.html) lo reaplica al abrir la app
  // para que un gym en modo oscuro o con menú de color no vea un destello.
  if (!persistir) return;
  try {
    localStorage.setItem(CLAVE_TEMA_GUARDADO, JSON.stringify({
      primario: variables['--color-primario'], secundario: variables['--color-secundario'], modo, variables,
    }));
  } catch (_) { /* modo privado */ }
};

export default aplicarTema;
